export * from './dom';
export * from './format';
export * from './lazy-import';
export * from './os';
export * from './memory';
export * from './requestQueue';

import { toast } from 'react-toastify';

let _timeoutRef: NodeJS.Timeout | number = 0;

/**
 * Debounced timeout to execute last one only
 * @param fn delayed function
 * @date 2025/01/06
 */
export const debouncedTimeout = (fn: () => void) => {
  _timeoutRef && clearTimeout(_timeoutRef);
  _timeoutRef = setTimeout(fn, 300);
};

// === JUST FOR DEMO OF Typescript Generic capability ==
interface GenericFunctionType {
  <T>(x: T): T[];
}
export const duckGenericFunc: GenericFunctionType = (x) => [x];
// === END OF DEMO =====

/**
 * Move the target element to the back of arry
 *
 * @param array any array
 * @param elementIndex postion to move back
 * @returns
 */
export function moveElementToBack<T>(array: T[], elementIndex: number): T[] {
  const aCopy = [...array];
  aCopy.push(aCopy.splice(elementIndex, 1)[0]);
  return aCopy;
}

export function moveElementToFront<T>(array: T[], element: T): T[] {
  const aCopy = array.filter((e) => e !== element);
  return [element, ...aCopy];
}

/**
 * swap child at `i1` to positiion `i2`
 * @param array
 * @param i1 index for element 1
 * @param i2 index for element 2
 * @returns
 */
export const swapElements = (array: string[], i1: number, i2: number) => {
  const copy = [...array];
  copy[i1] = copy.splice(i2, 1, copy[i1])[0];
  return copy;
};

/**
 * swap two fields
 *
 * @param fields field list to change
 * @param fieldA field to go back
 * @param fieldB field to go front
 * @returns a new fields
 */
export const swapFields = (
  fields: string[],
  fieldA: string,
  fieldB: string,
) => {
  const indexA = fields.indexOf(fieldA);
  const indexB = fields.indexOf(fieldB);
  const copy = [...fields];
  copy[indexA] = copy.splice(indexB, 1, copy[indexA])[0];
  return copy;
};

/**
 * Move source element to the back of target element
 *
 * @param array any array
 * @param srcIndex source position
 * @param tarIndex target postion to put after it
 * @returns
 */
export const moveElementBackOF = (
  array: any[],
  srcIndex: number,
  tarIndex: number,
) => {
  const aCopy = [...array];
  const srcElement = aCopy.splice(srcIndex, 1)[0];
  aCopy.splice(tarIndex, 0, srcElement);
  return aCopy;
};

/**
 * Execute the first-in function
 * @param handler delayed function
 * @param timeout pending time to execute, 300 ms by default
 * @returns
 */
export const getThrottleFunction = (
  handler: (...args: unknown[]) => void,
  timeout = 300,
) => {
  let timerId: NodeJS.Timeout | null;
  return (...args: unknown[]) => {
    if (timerId) return;

    timerId = setTimeout(() => {
      handler.apply(this, args);
      timerId = null; // clear this task
    }, timeout);
  };
};

/**
 * Exectue the last-in function
 * @param handler
 * @param timeout
 * @returns
 */
export const getDebounceFunction = (
  handler: (...args: unknown[]) => void,
  timeout = 300,
) => {
  let timerId: NodeJS.Timeout;
  return (...args: unknown[]) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      handler.apply(this, args);
    }, timeout);
  };
};

/**
 * Debounce too many same popups occurrence at the short time!
 *
 * @param message string to popup
 * @param isError if the toast is error
 */
export const lazyNotify = getDebounceFunction(
  (msg: unknown, isError?: unknown) => {
    const options = { delay: 0, autoClose: 3000 };
    if (isError) {
      return toast.error(msg as string, options);
    }
    toast.info(msg as string, options);
  },
  500, // FIXME: bigger to reduce popups as possible - 2023/07/19
);

type PipFun<T, C = string> = (args: T[], ctx?: C) => T[];

/**
 * Pipe function to process data with multiple functions
 *
 * @date 2025/05/27
 * @param funs pipe function
 * @returns
 */
export function pipeFuncs<T, C = string>(
  ...funs: PipFun<T, C>[]
): PipFun<T, C> {
  const innerPipe = (args: T[], ctx?: C) => {
    return funs.reduce((lastResult, fun) => fun(lastResult, ctx), args);
  };
  return innerPipe;
}
